import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';

import { UserHTTPService } from './http';

import { UserType } from '../../utils';
import { UserStatus } from '../../enums';
import { userLocalStorage } from '../../constants';
import { PaginatedList, AppResult } from '../../models';
import { environment } from '../../../../environments/environment';
import { ChangePasswordModel, UserModel, GetUsersModel, CreateUserModel, UpdateUserModel } from '../../models/auths';


@Injectable({
   providedIn: 'root',
})
export class UserService implements OnDestroy {
   // private readonly fields
   private readonly unsubscribe: Subscription[] = [];

   // public fields
   currentUser$: Observable<UserType>;
   isLoading$: Observable<boolean>;
   currentUserSubject: BehaviorSubject<UserType>;
   isLoadingSubject: BehaviorSubject<boolean>;

   get defaultUser(): UserModel {
      return {
         dateOfBirth: new Date(),
         email: '',
         firstName: '',
         gender: '',
         id: '',
         imageURL: '',
         isOnline: false,
         jobId: 0,
         jobName: '',
         lastName: '',
         phoneNumber: '',
         countryId: 0,
         countryName: '',
         stateId: 0,
         stateName: '',
         districtId: 0,
         //district: '',
         districtName: '',
         streat: '',
         userName: '',
         branchId: 0,
         branchCompanyId: 0,
         branchName: '',
         branchArabicName: '',
         userStatus: UserStatus.active
      };
   }

   get currentUserValue(): UserType {
      if (this.currentUserSubject.value) {
         return this.currentUserSubject.value;
      }

      const User = this.getUserFromLocalStorage();
      this.currentUserSubject.next(User);
      return this.currentUserSubject.value;
   }

   set currentUserValue(User: UserType) {
      this.currentUserSubject.next(User);
   }

   constructor(private readonly UserHttpService: UserHTTPService) {
      this.isLoadingSubject = new BehaviorSubject<boolean>(false);
      this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
      this.currentUser$ = this.currentUserSubject.asObservable();
      this.isLoading$ = this.isLoadingSubject.asObservable();
   }

   // public methods
   Search(model: GetUsersModel): Observable<PaginatedList<UserModel>> {
      return this.UserHttpService.Search(model).pipe(
         map((users: PaginatedList<UserModel>) => {
            if (users) {
               return users;
            }

            return this.getDefaultUser(model.page);
         }),
         catchError((err) => {
            console.error('User Search error:', err);
            return of(this.getDefaultUser(model.page));
         })
      );
   }

   GetUserProfile(userName: string): Observable<UserModel | undefined> {
      return this.UserHttpService.GetUser(userName).pipe(
         map((user: UserModel) => {
            if (user) {
               user.imageURL = environment.imageUrl + user.imageURL;
               this.setUserToLocalStorage(user);
               this.currentUserSubject.next(user);
               return user;
            }
            return undefined;
         }),
         catchError((err) => {
            console.error('Get User error:', err);
            return of(undefined);
         })
      );
   }

   GetUser(userName: string): Observable<UserModel | undefined> {
      return this.UserHttpService.GetUser(userName).pipe(
         map((user: UserModel) => user),
         catchError((err) => {
            console.error('Get User error:', err);
            return of(undefined);
         })
      );
   }

   CreateUser(model: CreateUserModel): Observable<AppResult<UserModel> | undefined> {
      this.isLoadingSubject.next(true);
      return this.UserHttpService.AddUser(model).pipe(
         map((result: AppResult<UserModel>) => result),
         catchError((err) => {
            console.error('Create user error:', err);
            return of(err.error);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   UpdateProfile(model: UpdateUserModel): Observable<AppResult<UserModel> | undefined> {
      this.isLoadingSubject.next(true);
      return this.UserHttpService.UpdateUser(model).pipe(
         map((result: AppResult<UserModel>) => {
            if (result.result && result.isSuccess) {
               result.result.imageURL = environment.imageUrl + result.result.imageURL;
               this.setUserToLocalStorage(result.result);
               this.currentUserSubject.next(result.result);
            }
            return result;
         }),
         catchError((err) => {
            console.error('Update profile error:', err);
            return of(err.error);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   UpdateUser(model: UpdateUserModel): Observable<AppResult<UserModel> | undefined> {
      this.isLoadingSubject.next(true);
      return this.UserHttpService.UpdateUser(model).pipe(
         map((result: AppResult<UserModel>) => result),
         catchError((err) => {
            console.error('Update user error:', err);
            return of(err.error);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   SuspendUser(userName: string): Observable<AppResult<UserModel> | undefined> {
      return this.UserHttpService.SuspendUser(userName).pipe(
         map((result: AppResult<UserModel>) => result),
         catchError((err) => {
            console.error('Create user error:', err);
            return of(err.error);
         })
      );
   }

   ActivateUser(userName: string): Observable<AppResult<UserModel> | undefined> {
      return this.UserHttpService.ActivateUser(userName).pipe(
         map((result: AppResult<UserModel>) => result),
         catchError((err) => {
            console.error('Create user error:', err);
            return of(err.error);
         })
      );
   }

   ChangePassword(model: ChangePasswordModel): Observable<boolean | undefined> {
      this.isLoadingSubject.next(true);
      return this.UserHttpService.ChangePassword(model).pipe(
         map((result: AppResult<string>) => {
            return result.isSuccess;
         }),
         catchError((err) => {
            console.error('Change passord error:', err);
            return of(undefined);
         }),
         finalize(() => this.isLoadingSubject.next(false))
      );
   }

   // private methods
   private setUserToLocalStorage(User: UserModel): boolean {
      // store User UserToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
      if (User?.userName && localStorage) {
         localStorage.setItem(userLocalStorage, JSON.stringify(User));
         return true;
      }
      return false;
   }

   private getUserFromLocalStorage(): UserModel | undefined {
      try {
         const lsValue = localStorage?.getItem(userLocalStorage);
         if (!lsValue) {
            return undefined;
         }

         const UserData = JSON.parse(lsValue);
         return UserData;
      } catch (error) {
         console.error(error);
         return undefined;
      }
   }

   private getDefaultUser(page: number): PaginatedList<UserModel> {
      return {
         items: [],
         pageNumber: page,
         totalCount: 0,
         totalPages: 0
      }
   }

   ngOnDestroy() {
      this.unsubscribe.forEach((sb) => sb.unsubscribe());
   }
}
